/**
 * RecorderStatusPill — visible "when can I see this in QC?" indicator.
 *
 * The fragmented MP4 / 60-s chunk architecture means clips become
 * locally previewable only after each chunk emits. The pill counts
 * down to that next emit + tells the operator how many parts are
 * already in IDB (i.e. how much footage is reviewable RIGHT NOW).
 *
 * Two states matter:
 *   - Recording: countdown to next chunk + parts captured so far
 *   - Idle: a one-liner about Stop-recording behaviour, so a fresh
 *     operator knows clips aren't durable until they hit Stop.
 */
import { useEffect, useState } from "react";
import { Clock, CheckCircle2, AlertCircle } from "lucide-react";


const CHUNK_MS = 60 * 1000;

function formatMs(ms) {
  if (ms == null || ms < 0) return "0:00";
  const secTotal = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(secTotal / 60);
  const s = secTotal % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}


export default function RecorderStatusPill({ recorder }) {
  // Local tick so the countdown updates every second even when the
  // parent doesn't re-render. (`recorder.elapsedMs` is a state setter
  // inside the hook ticking 5×/sec; that's enough resolution but we
  // also want the pill text to refresh smoothly.)
  const [, setTick] = useState(0);
  useEffect(() => {
    if (recorder?.state !== "recording") return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [recorder?.state]);

  const state    = recorder?.state || "idle";
  const elapsed  = recorder?.elapsedMs ?? 0;
  const uploaded = recorder?.uploadProgress?.uploaded ?? 0;
  const queued   = recorder?.uploadProgress?.queued ?? 0;

  // Time until the NEXT MediaRecorder.dataavailable fires. The recorder
  // starts emitting at multiples of CHUNK_MS, so msUntilNext is
  // CHUNK_MS - (elapsed % CHUNK_MS).
  const msUntilNext = state === "recording"
    ? CHUNK_MS - (elapsed % CHUNK_MS)
    : null;

  // 2026-05-29 — idle-state info pill removed per founder ask. It
  // explained how per-ball clips materialise after recording, but
  // ate 1-2 lines of vertical space in the left column on every
  // load. Operators already know how the recorder behaves; the
  // active-recording badge below still surfaces the timing info.
  if (state !== "recording") return null;

  // Currently recording — show countdown + parts uploaded so the
  // operator knows their footage is actually flowing.
  return (
    <div
      className="flex items-center gap-3 text-[11px] px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
      title={`Recording. Next minute of footage becomes reviewable in QC in ${formatMs(msUntilNext)}. ${uploaded} minute${uploaded === 1 ? "" : "s"} of footage saved so far.`}
    >
      <div className="flex items-center gap-1">
        <Clock className="w-3 h-3" />
        <span className="font-mono">Next clip-batch in {formatMs(msUntilNext)}</span>
      </div>
      <span className="text-emerald-400/40">·</span>
      <div className="flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" />
        <span className="font-mono">{uploaded} min{uploaded === 1 ? "" : "s"} saved</span>
        {queued > 0 && (
          <span className="text-amber-300/80 font-mono ml-1">({queued} uploading)</span>
        )}
      </div>
    </div>
  );
}
